import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { DashboardProvider } from './pages/dashboard/DashboardProvider';
import { CustomThemeProvider } from './pages/dashboard/ThemeProvider';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import CssBaseline from '@mui/material/CssBaseline';
import { HelmetProvider } from 'react-helmet-async';

// Lazy imports
const Login = lazy(() => import('./pages/auth/Login'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Custom loading component
const GoldLoadingScreen = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: 80,
        height: 80,
        margin: '0 auto 20px',
        position: 'relative',
        animation: 'spin 2s linear infinite',
      }}>
        <div style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #D4AF37 0%, #FFD700 100%)',
          boxShadow: '0 0 30px rgba(255, 215, 0, 0.5)',
        }}></div>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#000',
        }}>
          ₯
        </div>
      </div>
      <h3 style={{ 
        color: '#FFD700', 
        marginBottom: '10px',
        fontFamily: '"Inter", sans-serif',
        fontWeight: 600
      }}>
        Kabul Gold House
      </h3>
      <p style={{ 
        color: '#C0C0C0',
        fontFamily: '"Inter", sans-serif',
        fontSize: '0.875rem'
      }}>
        Loading secure portal...
      </p>
      <div style={{
        width: 200,
        height: 4,
        background: 'rgba(255, 215, 0, 0.2)',
        borderRadius: 2,
        margin: '20px auto',
        overflow: 'hidden'
      }}>
        <div style={{
          width: '60%',
          height: '100%',
          background: 'linear-gradient(90deg, #FFD700, #D4AF37)',
          animation: 'loading 2s ease-in-out infinite',
          borderRadius: 2
        }}></div>
      </div>
    </div>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes loading {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(200%); }
      }
    `}</style>
  </div>
);

// Role-based routing configuration
const roleRoutes = {
  owner: [
    '/dashboard',
    '/dashboard/*'
  ],
  manager: [
    '/dashboard',
    '/dashboard/*'
  ],
  sales: [
    '/dashboard',
    '/dashboard/*'
  ],
  accountant: [
    '/dashboard',
    '/dashboard/*'
  ],
  security: [
    '/dashboard',
    '/dashboard/*'
  ],
};

const hasPermission = (userRole, path) => {
  if (!userRole) return false;
  const allowedRoutes = roleRoutes[userRole] || [];
  
  return allowedRoutes.some(route => {
    if (route === '/') return path === '/';
    if (route === '/dashboard/*') return path.startsWith('/dashboard');
    return path === route || path.startsWith(route + '/');
  });
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = window.location;

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [pathname]);

  return null;
};

// Error boundary for route-level errors
const RouteErrorBoundary = ({ children }) => (
  <ErrorBoundary
    fallback={
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        color: 'white',
        textAlign: 'center',
        padding: '20px'
      }}>
        <div>
          <h2 style={{ color: '#FFD700' }}>Something went wrong</h2>
          <p>Please try refreshing the page or contact support.</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              color: 'black',
              fontWeight: 'bold',
              cursor: 'pointer',
              marginTop: '20px'
            }}
          >
            Refresh Page
          </button>
        </div>
      </div>
    }
  >
    {children}
  </ErrorBoundary>
);

function App() {
  useEffect(() => {
    const handleError = (error) => {
      console.error('Application error:', error);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled promise rejection:', event.reason);
    });

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleError);
    };
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <CustomThemeProvider>
          <AuthProvider>
            <DashboardProvider>
              <CssBaseline />
              <ScrollToTop />
              <RouteErrorBoundary>
                <Suspense fallback={<GoldLoadingScreen />}>
                  <Routes>
                    {/* Public Routes */}
                    <Route 
                      path="/login" 
                      element={<Login />} 
                    />
                    
                    {/* Protected Dashboard Route */}
                    <Route 
                      path="/dashboard/*" 
                      element={
                        <ProtectedRoute 
                          allowedRoles={['owner', 'manager', 'sales', 'accountant', 'security']}
                          hasPermission={hasPermission}
                        >
                          <Dashboard />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Redirect root to dashboard */}
                    <Route 
                      path="/" 
                      element={
                        <ProtectedRoute 
                          allowedRoles={['owner', 'manager', 'sales', 'accountant', 'security']}
                          hasPermission={hasPermission}
                        >
                          <Navigate to="/dashboard" replace />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* 404 - Not Found */}
                    <Route 
                      path="*" 
                      element={<NotFound />} 
                    />
                  </Routes>
                </Suspense>
              </RouteErrorBoundary>
              
              {/* Global Error Handler */}
              <GlobalErrorHandler />
            </DashboardProvider>
          </AuthProvider>
        </CustomThemeProvider>
      </Router>
    </HelmetProvider>
  );
}

// Global Error Handler Component
const GlobalErrorHandler = () => {
  useEffect(() => {
    const handleGlobalError = (error) => {
      console.error('Global error caught:', error);
    };

    window.addEventListener('error', (event) => {
      event.preventDefault();
      handleGlobalError(event.error);
    });

    return () => {
      window.removeEventListener('error', handleGlobalError);
    };
  }, []);

  return null;
};

// Add global styles
const globalStyles = `
  /* Smooth scrolling */
  html {
    scroll-behavior: smooth;
  }
  
  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  
  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 5px;
  }
  
  ::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #D4AF37 0%, #FFD700 100%);
    border-radius: 5px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #B8860B 0%, #D4AF37 100%);
  }
  
  /* Selection color */
  ::selection {
    background-color: rgba(212, 175, 55, 0.3);
    color: #000;
  }
  
  /* Focus styles for accessibility */
  :focus-visible {
    outline: 2px solid #D4AF37;
    outline-offset: 2px;
  }
  
  /* Dashboard layout fixes */
  .dashboard-main {
    transition: all 0.3s ease;
  }
`;

// Inject global styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = globalStyles;
  document.head.appendChild(styleSheet);
}

export default App;